import { useState } from 'react';
import Modal from '@components/widgets/Modal';
import Button from '@components/forms/Button';
import {
  CHECK_TYPE_OPTIONS,
  CONDITION_OPTIONS_MAP,
  InspectionStatusLabelEnum,
  RULE_TYPE_MAP
} from '@constants';
import { useInspectionDetailStore } from '@stores';
import { InspectionSecurityStatusEnum } from '@types';
import {
  openSecurityCheckDetail,
  isFailedInspectionRule,
  getInspectionCheckDocument
} from '../utils/securityCheckHandlers';
import {
  ruleTypeToConfig,
  ruleTypeToSecurityCheck
} from '../utils/ruleMappings';

interface InspectionFailurePanelProps {
  rules: any[];
  onResupplyRequest: () => void;
  onRequestException: () => void;
  isExceptionApprovalRequired?: boolean;
}

// Rule type reverse mapping for finding checkType and condition from ruleType
function getRuleTypeReverseMap() {
  const reverse: Record<string, { checkType: string; condition: string }> = {};
  Object.entries(RULE_TYPE_MAP).forEach(([checkType, condMap]) => {
    Object.entries(condMap as any).forEach(([condition, ruleType]) => {
      reverse[ruleType as string] = { checkType, condition };
    });
  });
  return reverse;
}

const InspectionFailurePanel = ({
  rules,
  onResupplyRequest,
  onRequestException,
  isExceptionApprovalRequired = false
}: InspectionFailurePanelProps) => {
  const [selectedRule, setSelectedRule] = useState<any>(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const {
    setActivePanel,
    setCollapse,
    setActivity,
    setRightBarStatus,
    setVariant,
    setStatusChipLabel,
    setActiveStep,
    inspectionDetailData,
    setShowCommentHistory
  } = useInspectionDetailStore();

  if (!rules || rules.length === 0) return null;

  const failedRules = rules.filter(isFailedInspectionRule);

  if (failedRules.length === 0) return null;

  const reverseMap = getRuleTypeReverseMap();

  const handleAddComment = (rule: any) => {
    const panelConfig = ruleTypeToConfig[rule.ruleType];
    if (!panelConfig) return;

    const securityCheck = ruleTypeToSecurityCheck[rule.ruleType];
    if (!securityCheck) return;

    // Determine active step based on rule type
    let activeStep = '1'; // Default to license checks
    if (
      rule.ruleType.startsWith('ASSET_') ||
      rule.ruleType.startsWith('MARINE_')
    ) {
      activeStep = '2';
    } else if (rule.ruleType.startsWith('REGO_')) {
      activeStep = '3';
    } else if (rule.ruleType.startsWith('BANK_')) {
      activeStep = '4';
    }

    // Get the actual documentObj from inspectionDetailData
    const items = inspectionDetailData?.inspectionChecks;
    let documentObj: any = null;

    if (items) {
      documentObj = getInspectionCheckDocument(
        items,
        securityCheck.key,
        activeStep,
        inspectionDetailData.assetType
      );
    }

    // Fallback to minimal object if no data found
    if (!documentObj) {
      documentObj = { type: securityCheck.type };
    }

    // Use the actual check status for the right-panel chip. The failed rule is
    // represented separately by ApprovalRequest / Criteria Not Met tags — do not
    // overwrite photo WARNING (etc.) with a synthetic FAIL string.
    // forceOpen: rule navigation must still open when the check status alone
    // would be non-clickable (e.g. UNREGISTERED / NO_RECORD / PASS+isExpired).
    const checkStatus =
      documentObj?.status ||
      (rule.status === 'IN_REVIEW'
        ? 'IN_REVIEW'
        : InspectionSecurityStatusEnum.failed);

    // Set the active step first
    setActiveStep(activeStep);

    // Close and reopen the panel to ensure fresh state
    setCollapse(true);

    // Use requestAnimationFrame to ensure DOM updates before opening
    requestAnimationFrame(() => {
      openSecurityCheckDetail(
        checkStatus,
        securityCheck.key,
        securityCheck.type,
        documentObj,
        false, // isNotMatch
        activeStep,
        {
          setCollapse,
          setActivePanel,
          setActivity,
          setRightBarStatus,
          setVariant,
          setStatusChipLabel,
          setShowCommentHistory
        },
        undefined,
        { forceOpen: true }
      );
    });
  };

  const closeModal = () => {
    setShowCommentModal(false);
    setSelectedRule(null);
  };

  // If isExceptionApprovalRequired is not true, show the original simple version
  if (!isExceptionApprovalRequired) {
    return (
      <div className="p-4 bg-[#fef3f2] border border-warning-200 rounded-lg mb-4">
        <div className="flex flex-col md:flex-row">
          {/* Left side - Explanation text */}
          <div className="md:w-1/2 pr-4">
            <p className="text-gray-700 text-[16px] font-semibold mb-2">
              This inspection has not met one or more configured acceptance
              rules and cannot be submitted.
            </p>
            <p className="text-gray-700 text-[16px] font-semibold mb-2">
              Review the failed checks. You can either request updated
              documentation from the seller to resolve the issues, or request an
              exception.
            </p>
          </div>

          {/* Right side - Failed criteria list */}
          <div className="md:w-1/2 md:pl-4 md:border-l border-gray-400">
            <p className="text-gray-700 text-[16px] font-semibold mb-2">
              Failed Criteria:
            </p>
            <ul className="list-disc ml-6">
              {failedRules.map((rule, index) => {
                const ruleInfo = reverseMap[rule.ruleType];
                if (!ruleInfo) return null;

                const checkTypeOption = CHECK_TYPE_OPTIONS.find(
                  (opt) => opt.value === ruleInfo.checkType
                );
                const conditionOption = CONDITION_OPTIONS_MAP[
                  ruleInfo.checkType
                ]?.find((opt) => opt.value === ruleInfo.condition);

                if (!checkTypeOption || !conditionOption) return null;

                const isInReview = rule.status === 'IN_REVIEW';
                const isApproved = rule.exception === 'EXCEPTION_GRANTED';
                const isApprovalRequired =
                  rule.exception === 'APPROVAL_REQUIRED';

                return (
                  <li key={index} className="text-gray-700 mb-1">
                    <span className="font-medium">
                      {checkTypeOption.label}: {conditionOption.label}
                    </span>
                    {(isInReview || isApprovalRequired) && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-light/10 border border-secondary-light text-secondary-light">
                        {InspectionStatusLabelEnum.InReview}
                      </span>
                    )}
                    {isApproved && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success text-white">
                        Approved
                      </span>
                    )}
                    {!isInReview && !isApprovalRequired && !isApproved && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-error-50 text-error-700 border border-error-300">
                        Criteria Not Met
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Bottom - Action buttons */}
        <div className="flex flex-col md:flex-row gap-4 mt-6">
          <Button
            onClick={() => onResupplyRequest()}
            className="w-full md:w-[193px] bg-white border border-gray-300 hover:bg-gray-400 text-gray-800 text-sm font-[600] py-2 px-4 rounded-lg inline-flex items-center">
            <span className="flex items-center">Resupply Request</span>
          </Button>

          <Button
            onClick={() => onRequestException()}
            className="w-full md:w-auto bg-primary text-white text-sm font-[600] py-2 px-4 rounded-lg inline-flex items-center">
            <span className="flex items-center">Request Exception</span>
          </Button>
        </div>
      </div>
    );
  }

  // Otherwise show the advanced approval version
  return (
    <>
      <div className="p-6 bg-[#fef3f2] border border-warning-200 rounded-lg mb-4">
        <div className="flex flex-col">
          {/* Content container with left-right layout */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left side content */}
            <div className="lg:w-1/2 lg:pr-6">
              {/* Header */}
              <h3 className="text-xl font-semibold text-neutral-900 mb-4">
                Action Required
              </h3>
              <p className="text-neutral-900 text-base mb-2">
                This inspection does not yet meet your lender&apos;s
                requirements and cannot be submitted.
              </p>
              <p className="text-neutral-900 text-base mb-4">
                To complete the inspection:
              </p>
              <ul className="list-disc ml-6 space-y-2 text-base text-neutral-900">
                <li>
                  <button
                    onClick={() => onResupplyRequest()}
                    className="font-semibold hover:text-primary-dark underline">
                    Resupply Request
                  </button>
                  <span>
                    {' '}
                    – when the seller can provide updated or corrected
                    documents.
                  </span>
                </li>
                <li>
                  <span className="font-semibold">Add Comment</span> – when you
                  believe the check is acceptable or a false positive, and want
                  the lender to review and approve.
                </li>
              </ul>
              <p className="text-neutral-900 text-base mt-4">
                Once all failed checks are either resolved or &apos;{InspectionStatusLabelEnum.InReview}&apos;, the lender will be notified.
              </p>
            </div>

            {/* Vertical Divider */}
            <div className="hidden lg:block w-px bg-neutral-200"></div>

            {/* Right side - Failed Criteria section */}
            <div className="lg:w-1/2 lg:pl-6">
              {/* Single card containing all failed criteria */}
              <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                {/* Card Header */}
                <div className="bg-neutral-50 px-4 py-3 border-b border-neutral-200">
                  <h4 className="text-neutral-900 text-base font-semibold">
                    Failed Criteria:
                  </h4>
                </div>
                {/* Card Body */}
                <div className="px-4">
                  <div className="space-y-1">
                    {failedRules.map((rule, index) => {
                      const ruleInfo = reverseMap[rule.ruleType];
                      if (!ruleInfo) return null;

                      const checkTypeOption = CHECK_TYPE_OPTIONS.find(
                        (opt) => opt.value === ruleInfo.checkType
                      );
                      const conditionOption = CONDITION_OPTIONS_MAP[
                        ruleInfo.checkType
                      ]?.find((opt) => opt.value === ruleInfo.condition);

                      if (!checkTypeOption || !conditionOption) return null;

                      const isInReview = rule.status === 'IN_REVIEW';
                      const isApproved = rule.exception === 'EXCEPTION_GRANTED';
                      const isApprovalRequired =
                        rule.exception === 'APPROVAL_REQUIRED';

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between py-3 last:border-0">
                          <div className="flex items-center gap-3 mr-2">
                            <span className="text-neutral-900 text-base font-semibold">
                              {checkTypeOption.label} – {conditionOption.label}
                            </span>
                            {(isInReview || isApprovalRequired) && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-light/10 border border-secondary-light text-secondary-light whitespace-nowrap">
                                {InspectionStatusLabelEnum.InReview}
                              </span>
                            )}
                            {isApproved && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-success text-white whitespace-nowrap">
                                Approved
                              </span>
                            )}
                            {!isInReview &&
                              !isApprovalRequired &&
                              !isApproved && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-error-50 text-error-700 border border-error-300 whitespace-nowrap">
                                  Criteria Not Met
                                </span>
                              )}
                          </div>
                          <button
                            onClick={() => handleAddComment(rule)}
                            className="text-neutral-900 hover:text-neutral-900 text-base font-semibold underline inline-flex items-center gap-1 whitespace-nowrap">
                            Add Comment
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                              className="w-4 h-4">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25"
                              />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comment Modal - if needed for modal-based approach */}
      {showCommentModal &&
        selectedRule &&
        (() => {
          const panelConfig = ruleTypeToConfig[selectedRule.ruleType];
          if (!panelConfig) return null;
          const Component = panelConfig.component;
          return (
            <Modal open={showCommentModal} onClose={closeModal} size="lg">
              <Component type={panelConfig.type} status={selectedRule.status} />
            </Modal>
          );
        })()}
    </>
  );
};

export default InspectionFailurePanel;
